import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardHeader } from './DashboardHeader';
import { PitchSummary } from './PitchSummary';
import { FileText, Calculator, Video, Building2, Users2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from "./ui/card";

interface SummaryData {
    id: number;
    summary_metrics: {
        scores: {
            product: number;
            market: number;
            financial: number;
            vision: number;
        };
        summary: {
            product: string;
            market: string;
            financial: string;
            vision: string;
        };
    };
    created_at: string;
}

interface ValuationData {
    suggested_valuation: number;
    confidence_level: string;
    metrics: {
        market_score: number;
        product_score: number;
        financial_score: number;
        vision_score: number;
        overall_score: number;
    };
    industry: string;
    factors: {
        market: string;
        product: string;
        financial: string;
        vision: string;
    };
}

const Summary: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { api, isAuthenticated } = useAuth();
    const [summary, setSummary] = useState<SummaryData | null>(null);
    const [valuation, setValuation] = useState<ValuationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const features = [
        {
            Icon: FileText,
            name: "View Analysis",
            description: "Get AI-powered feedback on your pitch deck. Our analysis covers structure, content, and presentation to help you create a compelling story for investors.",
            href: `/analysis/${id}`,
            cta: "View complete analysis",
        },
        {
            Icon: Calculator,
            name: "Valuation",
            description: "Understand your startup's worth with our comprehensive valuation tools. Get insights based on market data and industry benchmarks.",
            href: `/valuation/${summary?.id || ''}`,
            cta: "Calculate valuation",
        },
        {
            Icon: Video,
            name: "Investor Interview",
            description: "Practice your pitch with our AI interviewer. Get real-time feedback on your presentation skills and common investor questions.",
            href: "/interviews",
            cta: "Start VC Interview",
        },
        {
            Icon: Building2,
            name: "Data Room",
            description: "Manage your fundraising process in one place. Track investor interactions, documents, and deal progress efficiently.",
            href: "/deal-room",
            cta: "Enter deal room",
        },
        {
            Icon: Users2,
            name: "Team",
            description: "Showcase your founding team's expertise, experience, and unique qualifications. Highlight the diverse skills and track record that make your team uniquely positioned for success.",
            href: `/founders/${id}`,
            cta: "Find out about the Team",
        },
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Only fetch summary data
                const summaryResponse = await api.get(`/api/summary/${id}`);
                setSummary(summaryResponse.data);
                setError(null);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to fetch data');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchData();
        }
    }, [api, id, isAuthenticated]);

    const formatCurrency = (value: number): string => {
        if (value >= 1000000000) {
            return `$${(value / 1000000000).toFixed(1)}B`;
        } else if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`;
        }
        return `$${value.toLocaleString()}`;
    };

    const getConfidenceBadgeColor = (level: string): string => {
        switch (level.toLowerCase()) {
            case 'high':
                return 'bg-green-100 text-green-800';
            case 'medium':
                return 'bg-yellow-100 text-yellow-800';
            case 'low':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="container mx-auto py-8">
                {/* DashboardHeader is already included in the ProtectedRoute wrapper */}
                {loading ? (
                    <div className="text-center mt-8">Loading...</div>
                ) : error ? (
                    <div className="text-center mt-8 text-red-500">{error}</div>
                ) : (
                    <div className="space-y-8 mt-8">
                        <PitchSummary summaryMetrics={summary?.summary_metrics} analysisResults={{}} />
                        
                        {/* Valuation Card */}
                        {valuation && (
                            <Card className="p-6 space-y-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Suggested Valuation</h3>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">
                                            {formatCurrency(valuation.suggested_valuation)}
                                        </p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceBadgeColor(valuation.confidence_level)}`}>
                                        {valuation.confidence_level.charAt(0).toUpperCase() + valuation.confidence_level.slice(1)} Confidence
                                    </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Industry</h4>
                                        <p className="text-sm text-gray-900">{valuation.industry}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Overall Score</h4>
                                        <p className="text-sm text-gray-900">{valuation.metrics.overall_score.toFixed(1)}/100</p>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-gray-500 mb-2">Key Metrics</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Market</span>
                                            <span className="text-sm font-medium">{valuation.metrics.market_score.toFixed(1)}/100</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Product</span>
                                            <span className="text-sm font-medium">{valuation.metrics.product_score.toFixed(1)}/100</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Financial</span>
                                            <span className="text-sm font-medium">{valuation.metrics.financial_score.toFixed(1)}/100</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Vision</span>
                                            <span className="text-sm font-medium">{valuation.metrics.vision_score.toFixed(1)}/100</span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* Features Grid */}
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {features.slice(0, 3).map((feature, index) => (
                                    <Link
                                        key={feature.name}
                                        to={feature.href}
                                        className="block"
                                    >
                                        <Card className="h-[280px] flex flex-col">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <feature.Icon className="h-6 w-6 text-blue-600" />
                                                <h3 className="text-lg font-semibold">{feature.name}</h3>
                                            </div>
                                            <p className="text-sm text-gray-500 mb-4 flex-grow">
                                                {feature.description}
                                            </p>
                                            <div className="pt-4 border-t border-gray-100">
                                                <span className="text-sm font-medium text-blue-600 group-hover:text-blue-700">
                                                    {feature.cta} →
                                                </span>
                                            </div>
                                        </Card>
                                    </Link>
                                ))}
                            </div>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 mt-8 lg:w-2/3 mx-auto">
                                {features.slice(3).map((feature, index) => (
                                    <Link
                                        key={feature.name}
                                        to={feature.href}
                                        className="block"
                                    >
                                        <Card className="h-[280px] flex flex-col">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <feature.Icon className="h-6 w-6 text-blue-600" />
                                                <h3 className="text-lg font-semibold">{feature.name}</h3>
                                            </div>
                                            <p className="text-sm text-gray-500 mb-4 flex-grow">
                                                {feature.description}
                                            </p>
                                            <div className="pt-4 border-t border-gray-100">
                                                <span className="text-sm font-medium text-blue-600 group-hover:text-blue-700">
                                                    {feature.cta} →
                                                </span>
                                            </div>
                                        </Card>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Summary;
